// YouTube Tool — pull metadata / transcript / comments from any YouTube URL into the
// Workbench. Lives directly below the AI Assistant. Single shared results pane that
// gets REPLACED per click (the staging area is the user's scratchpad). When the
// pane already has data, a red guard dialog forces the user to transfer-or-discard
// before fetching the next pull. Smart "questions only + creator-handle reply"
// filter keeps the comments pull tiny.
//
// Per-creator BYOK for the Data API v3 key — set once on the user record via
// PATCH /api/auth/youtube-key. Transcript endpoint needs no key (timed-text scrape).

import { useEffect, useRef, useState } from "react";
import {
  YoutubeLogo, Info, ArrowsClockwise, ArrowDown, ArrowSquareIn, ChatTeardropDots,
  TextT, ListBullets, Robot, Question, Eye, Trash, X, FilmStrip, TextStrikethrough, ArrowUp,
  CaretDown, CaretRight, Eraser, FloppyDisk, Check, Play,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LockGate from "@/components/LockGate";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { useEntitlements } from "@/lib/entitlements";
import KeyInstructionsPopover from "@/components/KeyInstructionsPopover";
import InfoTip from "@/components/InfoTip";

function MetadataView({ data }) {
  return (
    <div className="space-y-3 text-sm" data-testid="yt-result-metadata">
      <div className="font-serif-display text-xl">{data.title || "(untitled)"}</div>
      <div className="text-xs text-[#6B6A68]">
        <a href={data.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[#4A5D4E] break-all">{data.url}</a>
      </div>
      {data.description && (
        <div className="bg-[#FDFCF8] border hairline rounded p-3 max-h-60 overflow-y-auto whitespace-pre-wrap text-[12.5px] leading-relaxed">{data.description}</div>
      )}
    </div>
  );
}

function CommentsView({ data }) {
  return (
    <div className="space-y-3" data-testid="yt-result-comments">
      <div className="text-xs text-[#6B6A68]">
        Returned <span className="text-[#1C1B1A] font-medium">{data.total_returned.toLocaleString()}</span> comment{data.total_returned === 1 ? "" : "s"}
        {data.filter?.questions_only && " · questions-only filter active"}
        {data.filter?.creator_handle && <> · creator-reply match: <span className="font-mono">{data.filter.creator_handle}</span></>}
        {data.capped && " · CAP REACHED"}
      </div>
      <div className="max-h-[420px] overflow-y-auto space-y-3">
        {data.comments.map((c) => (
          <div key={c.id} className="bg-[#FDFCF8] border hairline rounded p-3 text-[12.5px]">
            <div className="text-[#1C1B1A] flex items-center gap-2 mb-1">
              <span className="font-medium">{c.author}</span>
              <span className="text-[#6B6A68] text-[10px]">{c.published_at?.slice(0, 10)}</span>
              {c.like_count > 0 && <span className="text-[10px] text-[#6B6A68]">· {c.like_count} ❤</span>}
            </div>
            <div className="whitespace-pre-wrap text-[#1C1B1A]">{c.text}</div>
            {c.replies?.length > 0 && (
              <div className="mt-2 ml-4 pl-3 border-l-2 hairline space-y-2">
                {c.replies.map((r) => (
                  <div key={r.id} className="text-[12px]">
                    <div className="text-[#1C1B1A] font-medium inline">{r.author}: </div>
                    <span className="text-[#1C1B1A] whitespace-pre-wrap">{r.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function commentsToText(data) {
  const lines = [];
  for (const c of data.comments || []) {
    lines.push(`[${c.author}] ${c.text}`);
    for (const r of c.replies || []) lines.push(`  ↳ [${r.author}] ${r.text}`);
    lines.push("");
  }
  return lines.join("\n").trim();
}

function metadataToText(data) {
  return [
    `Title: ${data.title}`,
    `URL: ${data.url}`,
    "",
    "Description:",
    data.description || "",
  ].join("\n").trim();
}

function HelpHoverCard() {
  // Hoverable help popup: opens on hover/focus, CLOSES only after the cursor
  // leaves BOTH the trigger and the popup for 180 ms. That grace window is
  // enough to move the cursor from the eye into the popup to highlight text.
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const cancelClose = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };
  useEffect(() => () => cancelClose(), []);
  return (
    <span className="relative inline-block" onMouseEnter={() => { cancelClose(); setOpen(true); }} onMouseLeave={scheduleClose}>
      <button
        type="button"
        onFocus={() => setOpen(true)}
        onBlur={scheduleClose}
        aria-label="How to set up your YouTube API key"
        data-testid="yt-help-button"
        className="inline-flex items-center justify-center w-5 h-5 rounded-full border hairline text-[#6B6A68] hover:border-[#4A5D4E] hover:text-[#4A5D4E]"
      >
        <Eye size={11} weight="duotone" />
      </button>
      {open && (
        <span
          role="tooltip"
          data-testid="yt-help-popup"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="absolute left-0 top-7 z-30 w-80 bg-[#1C1B1A] text-[#FDFCF8] text-[11px] leading-relaxed p-3 rounded-md shadow-lg text-left select-text cursor-text"
        >
          <strong>Each creator brings their own YouTube key.</strong>
          <ol className="mt-1 list-decimal pl-4 space-y-0.5">
            <li>
              Open <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#F0F1EE] break-all">https://console.cloud.google.com/apis/credentials</a>
            </li>
            <li>Create a project (or pick an existing one).</li>
            <li>
              API Library → enable <span className="font-mono">YouTube Data API v3</span>.
            </li>
            <li>Credentials → <em>Create Credentials</em> → <em>API Key</em>.</li>
            <li>Copy the key (starts with <span className="font-mono">AIzaSy…</span>) and paste it into "Add key".</li>
          </ol>
          <div className="mt-1 opacity-70">Free tier: 10,000 units/day · transcripts cost 0 units.</div>
        </span>
      )}
    </span>
  );
}

export default function YouTubeTool({ siloId, clearRequest, macroApi, workbenchMacroApiRef, user, onAppendToStaging, onSendToSlot, onSendToAssistant, onUserUpdated, slotCount = 3, surface = "workbench" }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const { locked: cpLocked, lockStyle: cpLockStyle } = useEntitlements();
  const { openGate: cpOpenGate } = useCreatorProGate();
  const ytLocked = cpLocked("youtube_twin");
  const [busyKind, setBusyKind] = useState(null); // which pull kind is fetching (for glimmer)
  const [pull, setPull] = useState(null); // { kind: "metadata"|"transcript"|"comments", data, text }
  const [keyOpen, setKeyOpen] = useState(false);
  const [questionsOnly, setQuestionsOnly] = useState(true);
  const [creatorHandle, setCreatorHandle] = useState("");
  // Feb 2026 (Randy pt-yt-lib-search) — inline search over the active
  // library's rows. Filters by video title AND URL (video_id substring),
  // case-insensitive. Empty string → show all.
  const [librarySearch, setLibrarySearch] = useState("");
  const [pendingFetch, setPendingFetch] = useState(null);
  // Verbatim dedup of the description against the silo's existing content (all 3
  // tiers). Runs at transfer time (Staging + Slots). No LLM — instant + free.
  const [skipExisting, setSkipExisting] = useState(true);
  const [skippedPassages, setSkippedPassages] = useState([]); // blocks dropped on last transfer
  const [skippedOpen, setSkippedOpen] = useState(false);
  const [transferBusy, setTransferBusy] = useState(false);
  const hasKey = !!user?.has_youtube_key;

  // ----- Creator's Video Library state -----
  // Persistent grid of a YouTube channel's full upload list. Survives logout/login
  // (server-side state in `yt_video_libraries` collection). Each row tracks
  // `struck` and `deleted` flags so a creator can methodically work through a
  // backlog over many sessions without losing their place.
  const [libChannelInput, setLibChannelInput] = useState("");
  const [libRangeFrom, setLibRangeFrom] = useState(1);
  const [libRangeTo, setLibRangeTo] = useState(50);
  const [libBusy, setLibBusy] = useState(false);
  const [libraries, setLibraries] = useState([]);
  const [activeLibId, setActiveLibId] = useState(null);
  const rootRef = useRef(null);
  const headerRef = useRef(null);
  const handoffTitleRef = useRef("");
  const [macroCta, setMacroCta] = useState(false);

  const activeLibrary = libraries.find((l) => l.id === activeLibId) || null;

  const loadLibraries = async () => {
    if (!siloId) return;
    try {
      const r = await api.get("/yt/library", { params: { silo_id: siloId, surface } });
      setLibraries(r.data || []);
      if (!activeLibId && (r.data || []).length > 0) setActiveLibId(r.data[0].id);
    } catch { /* silent — user may have no key yet */ }
  };
  useEffect(() => { loadLibraries(); /* eslint-disable-next-line */ }, [siloId]);

  const fetchLibraryRange = async () => {
    const ref = libChannelInput.trim();
    if (!ref) { toast.error("Paste a channel URL or @handle first."); return; }
    const fromN = Math.max(1, Math.floor(Number(libRangeFrom) || 1));
    const toN = Math.max(fromN, Math.floor(Number(libRangeTo) || fromN));
    const offset = fromN - 1;
    const limit = toN - fromN + 1;
    if (limit > 500) { toast.error("Range too wide — max 500 videos per fetch."); return; }
    setLibBusy(true);
    try {
      const r = await api.post("/yt/library/fetch", { channel_ref: ref, offset, limit, silo_id: siloId, surface });
      const lib = r.data?.library;
      if (!lib) throw new Error("No library returned.");
      setLibraries((prev) => {
        const without = prev.filter((p) => p.id !== lib.id);
        return [lib, ...without];
      });
      setActiveLibId(lib.id);
      const fetched = r.data?.fetched_this_call || 0;
      toast.success(`Fetched ${fetched} video${fetched === 1 ? "" : "s"} · channel total: ${lib.total_videos.toLocaleString()}`);
      // Auto-advance the range so consecutive clicks pull the next slice.
      setLibRangeFrom(toN + 1);
      setLibRangeTo(Math.min(toN + limit, lib.total_videos || (toN + limit)));
    } catch (e) {
      const detail = e?.response?.data?.detail || "Library fetch failed.";
      toast.error(detail);
    } finally { setLibBusy(false); }
  };

  const updateRow = async (libId, video_id, patch) => {
    // Optimistic update — flip the local flag immediately, server confirms.
    setLibraries((prev) => prev.map((l) => l.id !== libId ? l : ({
      ...l,
      rows: (l.rows || []).map((r) => r.video_id === video_id ? { ...r, ...patch, ...(patch.deleted ? { struck: true } : {}) } : r),
    })));
    try {
      await api.patch(`/yt/library/${libId}/row`, { video_id, ...patch });
      // Feb 17, 2026 (Randy pt-strike-live-sync) — the backend also
      // mutates `youtube_integrations.monitored_video_ids` when a strike
      // flips (bidirectional). Broadcast a DOM event so any Video Monitor
      // picker currently open refetches without a page refresh. Payload
      // is deliberately minimal — receivers do a full state refetch.
      window.dispatchEvent(new CustomEvent("mysilo:yt-strike-changed", {
        detail: { library_id: libId, video_id, patch, silo_id: siloId, surface },
      }));
    } catch {
      toast.error("Could not save row update — refreshing.");
      loadLibraries();
    }
  };

  // Jun 2026 (Randy) — "this video is in the silo" in ONE server call.
  // /yt/processed owns the whole chain (durable ledger → library row, created
  // if missing → struck → monitored). Before this, the only record of
  // "processed" was a strikethrough inside a deletable library, and it was
  // never written at all when the creator hadn't fetched the full library.
  //
  // Pending-marks queue: the mark is enqueued in localStorage BEFORE the call
  // and dropped on success, so a tab closing or a network blip between "macro
  // finished" and "mark landed" no longer loses the fact — it flushes on the
  // next mount. (Previously that strike was simply gone forever.)
  const PENDING_MARKS_KEY = "mysilo_yt_pending_marks";
  const readPendingMarks = () => {
    try { return JSON.parse(localStorage.getItem(PENDING_MARKS_KEY) || "[]"); } catch { return []; }
  };
  const writePendingMarks = (list) => {
    try { localStorage.setItem(PENDING_MARKS_KEY, JSON.stringify(list.slice(-50))); } catch { /* quota */ }
  };
  const dropPendingMark = (entry) => {
    writePendingMarks(readPendingMarks().filter(
      (p) => !(p.url === entry.url && p.silo_id === entry.silo_id && p.surface === entry.surface)));
  };
  const postMark = async (url, title) => {
    const r = await api.post("/yt/processed", {
      silo_id: siloId, surface, url, title: title || "",
    });
    // Same broadcast the strike PATCH uses, so an open Video Monitor picker
    // refreshes without a reload.
    window.dispatchEvent(new CustomEvent("mysilo:yt-strike-changed", {
      detail: { video_id: r.data?.video_id, silo_id: siloId, surface, patch: { struck: true } },
    }));
    return r.data;
  };
  const markProcessed = async (url, title) => {
    if (!siloId || !url) return null;
    const entry = { url, title: title || "", silo_id: siloId, surface, queued_at: Date.now() };
    writePendingMarks([...readPendingMarks().filter(
      (p) => !(p.url === entry.url && p.silo_id === entry.silo_id && p.surface === entry.surface)), entry]);
    try {
      const data = await postMark(url, title);
      dropPendingMark(entry);
      loadLibraries();
      return data;
    } catch {
      return null; // stays queued — retried on next mount
    }
  };

  // Flush any marks stranded by a closed tab / dropped connection.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mine = readPendingMarks().filter((p) => p.silo_id === siloId && p.surface === surface);
      if (!mine.length) return;
      let ok = 0;
      for (const p of mine) {
        if (cancelled) return;
        try {
          await postMark(p.url, p.title);
          dropPendingMark(p);
          ok += 1;
        } catch {
          break; // still offline / still failing — try again next mount
        }
      }
      if (ok && !cancelled) {
        loadLibraries();
        toast.success(`Recorded ${ok} processed video${ok === 1 ? "" : "s"} from an earlier session.`);
      }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [siloId, surface]);

  // Jun 2026 (Randy) — Build-as-you-go handoff. The tab dispatches a URL at
  // whichever YouTube Tool owns that silo + surface. Randy's correction: the
  // old block:"center" scroll dumped him "somewhere in the middle of the
  // Workbench" — the right place is THIS card's header. We also beacon the
  // "Run selected Macro" button, because that's the one action left.
  //
  // Scrolled twice on purpose: SiloManage runs its own window.scrollTo(0, 0)
  // when the tab changes, and a handoff from the Chats tab changes the tab.
  // The second pass (450ms) lands after that reset.
  const scrollToHeader = () => {
    const el = headerRef.current;
    if (!el) return;
    try {
      const stickyH = document.querySelector("header.sticky")?.getBoundingClientRect().height || 0;
      const y = el.getBoundingClientRect().top + window.scrollY - stickyH - 14;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    } catch { /* noop */ }
  };
  useEffect(() => {
    const onLoadUrl = (ev) => {
      const d = ev?.detail || {};
      if (!d.url) return;
      if (d.silo_id && d.silo_id !== siloId) return;
      if ((d.surface || "workbench") !== surface) return;
      setUrl(d.url);
      setPull(null);
      setPendingFetch(null);
      handoffTitleRef.current = d.title || "";
      setMacroCta(true);
      window.setTimeout(scrollToHeader, 120);
      window.setTimeout(scrollToHeader, 450);
      toast.success(d.title ? `Loaded "${d.title}" — run your macro.` : "Video URL loaded — run your macro.");
    };
    window.addEventListener("mysilo:yt-load-url", onLoadUrl);
    return () => window.removeEventListener("mysilo:yt-load-url", onLoadUrl);
    /* eslint-disable-next-line */
  }, [siloId, surface]);

  // The beacon is a nudge, not a nag: it gives up on its own.
  useEffect(() => {
    if (!macroCta) return;
    const t = window.setTimeout(() => setMacroCta(false), 25000);
    return () => window.clearTimeout(t);
  }, [macroCta]);

  const transferUrlToSlot = async (libId, row) => {
    if (!row?.url) return;
    setUrl(row.url);
    setPull(null);
    setPendingFetch(null);
    await updateRow(libId, row.video_id, { struck: true });
    toast.success(`URL loaded · "${(row.title || "").slice(0, 60)}"`);
    // Scroll the URL input into view so the user sees the swap.
    setTimeout(() => {
      try { document.querySelector('[data-testid="yt-url-input"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
    }, 80);
  };

  const deleteLibrary = async (libId) => {
    if (!window.confirm("Delete this entire video library entry? Your strike-throughs will be lost.")) return;
    try {
      await api.delete(`/yt/library/${libId}`);
      setLibraries((prev) => prev.filter((l) => l.id !== libId));
      if (activeLibId === libId) setActiveLibId(null);
      toast.success("Library entry deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const clearUrl = () => {
    setUrl("");
    setPull(null);
    setPendingFetch(null);
  };

  // Jun 2026 (Randy) — parent "Clear Workbench" button bumps `clearRequest`;
  // reset the video URL + output pane. Jul 2 2026 (Randy): the comment @handle
  // is now PERMANENT — Clear Workbench must NOT wipe it. It persists until the
  // user changes or clears it themselves. Never on initial mount (clearRequest
  // starts at 0).
  useEffect(() => {
    if (clearRequest) {
      clearUrl();
      setSkippedPassages([]);
      setSkippedOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearRequest]);

  // Allow the Library tool (below) to push a URL up here when the user hits "Use"
  useEffect(() => {
    const onPush = (e) => {
      if (!e?.detail?.url) return;
      setUrl(e.detail.url);
      setPull(null);
      setPendingFetch(null);
      toast.success("Video URL loaded. Pick a pull.");
    };
    window.addEventListener("yt-set-video-url", onPush);
    return () => window.removeEventListener("yt-set-video-url", onPush);
  }, []);

  // Jul 2026 (Randy) — classify a YouTube pull failure so the macro engine can
  // decide whether to SKIP this video and keep going (missing/blocked/private
  // transcript) or HALT the whole batch (quota / bad key — retrying won't help).
  const classifyYtError = (status, detail) => {
    const s = String(detail || "").toLowerCase();
    if (status === 429 || s.includes("quota")) return "QUOTA";
    if (status === 401 || status === 412 || s.includes("api key") || s.includes("key is invalid") || s.includes("key not valid")) return "KEY";
    if (status === 404 && (s.includes("caption") || s.includes("transcript") || s.includes("disabled"))) return "NO_TRANSCRIPT";
    if (status === 404 || s.includes("unavailable") || s.includes("private") || s.includes("not found") || s.includes("no data")) return "UNAVAILABLE";
    if (status === 502) return "FETCH_FAILED";
    return "UNKNOWN";
  };

  const reallyFetch = async (kind) => {
    setBusy(true);
    setBusyKind(kind);
    setSkippedPassages([]);
    setSkippedOpen(false);
    let result = { ok: false, status: null, reason: null, code: null, kind, charCount: 0 };
    try {
      let res;
      let text;
      let display;
      if (kind === "metadata") {
        const r = await api.post("/yt/metadata", { url, silo_id: siloId });
        res = r.data; text = metadataToText(res); display = res;
      } else if (kind === "transcript") {
        const r = await api.post("/yt/transcript", { url, silo_id: siloId });
        res = r.data; text = res.transcript || ""; display = res;
      } else if (kind === "comments") {
        const r = await api.post("/yt/comments", {
          url,
          silo_id: siloId,
          questions_only: questionsOnly,
          creator_handle: questionsOnly ? creatorHandle.trim() : null,
        });
        res = r.data; text = commentsToText(res); display = res;
      }
      setPull({ kind, data: display, text });
      result = { ok: true, status: 200, reason: null, code: null, kind, charCount: (text || "").length };
      toast.success(`${kind} loaded · ${text.length.toLocaleString()} chars`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Pull failed.";
      const reason = typeof detail === "string" ? detail : "Pull failed.";
      result = { ok: false, status, reason, code: classifyYtError(status, reason), kind, charCount: 0 };
      if (status === 412) toast.error(reason);
      else toast.error(`${reason}${status ? ` (${status})` : ""}`);
    } finally {
      setBusy(false);
      setBusyKind(null);
    }
    return result;
  };

  // Build the text to transfer. For metadata pulls with the dedup box checked,
  // run the verbatim scan against the silo's existing content (all tiers) and
  // stage only the new blocks (Title + URL are always kept). No LLM — instant.
  const buildTransferText = async () => {
    if (pull?.kind === "metadata" && skipExisting && siloId) {
      const startedAt = Date.now();
      setTransferBusy(true);
      try {
        const r = await api.post("/yt/dedup-description", {
          silo_id: siloId,
          description: pull.data.description || "",
        });
        const keptText = (r.data?.kept_text || "").trim();
        const skipped = r.data?.skipped || [];
        const skippedCount = r.data?.skipped_line_count || 0;
        setSkippedPassages(skipped);
        if (skipped.length) setSkippedOpen(true);
        const parts = [`Title: ${pull.data.title}`, `URL: ${pull.data.url}`, ""];
        if (keptText) {
          parts.push("Description:", keptText);
        } else {
          parts.push("Description already in your silo — nothing new.");
        }
        if (skippedCount) {
          toast.success(`Skipped ${skippedCount} duplicate line${skippedCount === 1 ? "" : "s"} already in your silo.`);
        } else {
          toast.message("No duplicate lines found — nothing to skip.");
        }
        return parts.join("\n").trim();
      } catch {
        toast.error("Dedup scan failed — sending full description.");
        return pull.text;
      } finally {
        // Keep the "scanning" feedback on-screen for a beat — the match itself
        // is near-instant, so without this the glimmer would flash invisibly.
        const elapsed = Date.now() - startedAt;
        if (elapsed < 650) await new Promise((res) => setTimeout(res, 650 - elapsed));
        setTransferBusy(false);
      }
    }
    return pull.text;
  };

  // Main transfer-row click: dedup (if applicable) then route the text.
  const doTransfer = async (target) => {
    if (target.label === "Clear") { target.run(); return; }
    const targetMap = { "Staging": "staging", "Slot 1": "slot1", "Slot 2": "slot2", "Slot 3": "slot3" };
    if (targetMap[target.label]) macroApi?.record?.("yt_transfer", { target: targetMap[target.label] });
    const text = await buildTransferText();
    target.run(text);
  };

  const fetchPull = async (kind) => {
    if (ytLocked) { cpOpenGate("youtube_twin"); return; }
    if (!url.trim()) { toast.error("Paste a YouTube URL first."); return; }
    macroApi?.record?.("yt_fetch", { kind });
    if (busy) return;
    if (pull) {
      // Guard: results pane has data — force user to choose a destination first.
      setPendingFetch(() => () => reallyFetch(kind));
      return;
    }
    await reallyFetch(kind);
  };

  const transferAndContinue = async (target) => {
    const fn = pendingFetch;
    if (target.label === "Clear") {
      target.run();
    } else {
      const text = await buildTransferText();
      target.run(text);
    }
    setPull(null);
    if (fn) {
      setPendingFetch(null);
      await fn();
    }
  };

  const discardAndContinue = async () => {
    setPull(null);
    if (pendingFetch) {
      const fn = pendingFetch;
      setPendingFetch(null);
      await fn();
    }
  };

  const renderResults = () => {
    if (!pull) return <div className="text-xs text-[#6B6A68] italic py-8 text-center">Pull data with the buttons above to populate this pane.</div>;
    if (pull.kind === "metadata") return <MetadataView data={pull.data} />;
    if (pull.kind === "comments") return <CommentsView data={pull.data} />;
    if (pull.kind === "transcript") return (
      <div data-testid="yt-result-transcript" className="space-y-2">
        <div className="text-xs text-[#6B6A68]">{pull.data.char_count.toLocaleString()} chars{pull.data.truncated && " · TRUNCATED"}</div>
        <div className="bg-[#FDFCF8] border hairline rounded p-3 max-h-[420px] overflow-y-auto whitespace-pre-wrap text-[12.5px] leading-relaxed">{pull.text}</div>
      </div>
    );
    return null;
  };

  // Collapse state for the Creator's Video Library — when the user has hundreds of
  // rows loaded the section dominates the page; this lets them tuck it away without
  // losing the data. Persisted server-side as part of workbench-state (yt_lib_collapsed).
  const [libCollapsed, setLibCollapsed] = useState(false);
  // Hydrate libCollapsed from the workbench-state on mount/silo-change. The Workbench
  // already loads workbench-state for itself; we read the same record here so the
  // YouTubeTool gets the same persisted preference.
  useEffect(() => {
    if (!siloId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/silos/${siloId}/workbench-state`);
        if (!cancelled) {
          setLibCollapsed(!!r.data?.yt_lib_collapsed);
          // Persisted per-silo prefs. Both filters default ON — only an explicit
          // stored `false` turns them off, so absent/true → true.
          setSkipExisting(r.data?.yt_skip_existing !== false);
          setQuestionsOnly(r.data?.yt_questions_only !== false);
          setCreatorHandle(r.data?.yt_creator_handle || "");
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [siloId]);
  // Fire-and-forget persistence of a YouTube-tool preference for THIS silo.
  const persistWb = (patch) => {
    if (siloId) api.patch(`/silos/${siloId}/workbench-state`, patch).catch(() => {});
  };
  // Jul 2026 (Randy) — the comment @handle gets its OWN deliberate Save button
  // (a protected guard). It still auto-saves on blur, but pressing Save gives an
  // explicit, confirmed lock-in so the handle can never feel "lost". `handleSaved`
  // briefly flips the button to a green "Saved ✓" state.
  const [handleSaved, setHandleSaved] = useState(false);
  const handleSavedTimer = useRef(null);
  const saveHandle = () => {
    persistWb({ yt_creator_handle: creatorHandle.trim() });
    setHandleSaved(true);
    toast.success("Handle saved.", { id: "yt-handle-saved" });
    if (handleSavedTimer.current) clearTimeout(handleSavedTimer.current);
    handleSavedTimer.current = setTimeout(() => setHandleSaved(false), 1800);
  };
  const toggleLibCollapsed = () => {
    setLibCollapsed((v) => {
      const next = !v;
      // Fire-and-forget — workbench-state PATCH is idempotent.
      if (siloId) api.patch(`/silos/${siloId}/workbench-state`, { yt_lib_collapsed: next }).catch(() => {});
      return next;
    });
  };

  const transferTargets = pull ? [
    { label: "Staging", testid: "yt-transfer-staging", run: (text) => onAppendToStaging?.(text) },
    // Slot targets — capped at `slotCount` (the Refinery tab is single-slot, Jul 2026).
    ...[1, 2, 3].slice(0, Math.max(1, slotCount)).map((n) => (
      { label: `Slot ${n}`, testid: `yt-transfer-slot-${n}`, run: (text) => onSendToSlot?.(n, text) }
    )),
    // "Clear" wipes the result pane so a fresh pull can land without the
    // replace-guard dialog. (Used to be "AI Assistant" — moved out per user feedback;
    // the AI Assistant has its own input field above this tool anyway.)
    { label: "Clear", testid: "yt-transfer-clear", run: () => { setPull(null); setPendingFetch(null); setSkippedPassages([]); setSkippedOpen(false); } },
  ] : [];

  // Register an imperative API for the Workbench macro engine. No dep array — we
  // re-register every render so the player always invokes fresh-state closures.
  useEffect(() => {
    if (!macroApi?.registerApi) return;
    macroApi.registerApi({
      fetch: async (kind) => {
        const r = await reallyFetch(kind);
        if (!r.ok) {
          const err = new Error(r.reason || `YouTube ${kind} fetch failed`);
          err.__yt_code = r.code || "UNKNOWN";
          err.__yt_status = r.status;
          err.reason = r.reason || `YouTube ${kind} fetch failed`;
          throw err;
        }
        // Jul 2026 (Randy) — blank-transcript guard: a caption track that comes
        // back empty (or almost) is treated as "no transcript" so the batch skips
        // it and NEVER stages/commits an empty doc to a tier.
        if (kind === "transcript" && (r.charCount || 0) < 20) {
          const err = new Error("Blank transcript");
          err.__yt_code = "BLANK";
          err.reason = "Blank transcript — the captions returned almost no text.";
          throw err;
        }
      },
      transfer: async (target) => {
        if (!pull) throw new Error("No YouTube data to transfer — the fetch produced nothing.");
        const text = await buildTransferText();
        if (target === "staging") onAppendToStaging?.(text);
        else if (target === "assistant") onSendToAssistant?.(text);
        else if (target === "slot1") onSendToSlot?.(1, text);
        else if (target === "slot2") onSendToSlot?.(2, text);
        else if (target === "slot3") onSendToSlot?.(3, text);
      },
      setUrl: (u) => setUrl(u),
      getState: () => ({
        busy, hasPull: !!pull, url,
        // Jun 2026 — a singleUrl macro run has no library row to take a title
        // from, so the commit used to fall back to "Macro result <date>".
        // Prefer the pulled metadata title, then the Build-as-you-go handoff.
        pullTitle: pull?.data?.title || handoffTitleRef.current || "",
      }),
      // Bottom-most (oldest) video that isn't struck or deleted — the next to process.
      getNextUnstruckVideo: (excludeIds) => {
        if (!activeLibrary) return null;
        const skip = excludeIds instanceof Set ? excludeIds : new Set(excludeIds || []);
        const visible = (activeLibrary.rows || []).filter((r) => !r.deleted);
        for (let i = visible.length - 1; i >= 0; i--) {
          const row = visible[i];
          if (!row.struck && !skip.has(row.video_id)) {
            return { libId: activeLibrary.id, video_id: row.video_id, url: row.url, title: row.title || "" };
          }
        }
        return null;
      },
      loadVideo: (u) => { setUrl(u); setPull(null); setPendingFetch(null); },
      strikeVideo: (libId, video_id) => updateRow(libId, video_id, { struck: true }),
      unStrikeVideo: (libId, video_id) => updateRow(libId, video_id, { struck: false }),
      // Feb 17, 2026 (Randy pt-run-selected-macro-fix) → rewritten Jun 2026.
      // Idempotent "this video is now in the silo". USED to scan local library
      // rows for a URL match and silently do nothing when none matched — so
      // "paste a URL + Run selected Macro" on a channel whose library was
      // never fetched processed the video while leaving it un-struck and
      // un-monitored, making its comments invisible to YT Replies forever.
      // Now it posts to /yt/processed, which owns the whole chain: durable
      // ledger → library row (created if missing) → struck → monitored.
      // Keyed on a server-normalized video_id, so youtu.be / ?t=90 / shorts
      // URLs are one video instead of three.
      ensureVideoStruckByUrl: async (u) => {
        const target = (u || "").trim();
        if (!target) return null;
        return markProcessed(target);
      },
      // Jun 2026 (Randy) — drop the ledger row WITHOUT un-striking. The macro
      // skip path needs this: a batch strikes each video before processing and
      // deliberately leaves it struck for the skip report, but a video that
      // produced nothing must stop claiming its content is in the silo.
      markUnprocessed: async (videoId) => {
        if (!siloId || !videoId) return;
        try {
          await api.delete("/yt/processed", {
            params: { silo_id: siloId, surface, video_id: videoId },
          });
        } catch { /* non-fatal — the gap alerts will still catch it */ }
      },
    });
  });

  return (
    <div ref={rootRef} className="bg-white border hairline rounded-xl shadow-sm overflow-hidden" data-testid="youtube-tool">
      <div ref={headerRef} className="px-4 py-3 border-b hairline flex items-center gap-3" data-testid="youtube-tool-header">
        <div className="w-8 h-8 rounded-md bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center shrink-0">
          <YoutubeLogo size={16} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#1C1B1A]">YouTube Tool</div>
          <div className="text-xs text-[#6B6A68] flex items-center gap-1.5 flex-wrap">
            Pull metadata, transcripts, &amp; comments into your Workbench.
            <HelpHoverCard />
            <span className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${hasKey ? "bg-[#F0F1EE] text-[#4A5D4E]" : "bg-[#FDF1ED] text-[#C16E5A]"}`} data-testid="yt-key-status">
              {hasKey ? "key set" : "no key"}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setKeyOpen((v) => !v)}
          className="text-xs px-3 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5 shrink-0"
          data-testid="yt-key-toggle"
        >
          <Question size={12} /> {hasKey ? "Update key" : "Add key"}
        </button>
      </div>

      {keyOpen && <KeyEditor user={user} onSaved={(u) => { onUserUpdated?.(u); setKeyOpen(false); }} />}

      <div className="px-4 py-3 border-b hairline">
        <label className="block text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Video URL</label>
        <div className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…"
            data-testid="yt-url-input"
            className="flex-1 min-w-0 bg-[#FDFCF8] border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
          />
          {/* Feb 17, 2026 (Randy pt-run-selected-macro) — one-click shortcut
              that runs the user's currently-SELECTED macro (see the SELECTED
              pill on the Macros panel below) against the URL currently in
              this field. Sits next to the URL input; the URL input is
              slightly narrower now (still flex-1) to make room. */}
            <LockGate locked={cpLocked("macro_autobuild")} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("macro_autobuild")}>
            <button
              type="button"
              onClick={() => {
                const u = (url || "").trim();
                if (!u) { toast.error("Paste a YouTube URL first."); return; }
                const api = workbenchMacroApiRef?.current;
                if (!api?.runSelectedMacroOnUrl) { toast.error("Macros aren't ready yet — try again in a moment."); return; }
                setMacroCta(false);
                api.runSelectedMacroOnUrl(u);
              }}
              disabled={!url.trim()}
              data-testid="yt-run-selected-macro"
              title="Run the currently-selected macro (see the SELECTED pill on the Macros panel below) on the URL in this field."
              className={`text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5 shrink-0 disabled:opacity-50 ${macroCta ? "cta-beacon" : ""}`}
            >
              <Play size={12} weight="fill" className="text-[#B54848]" /> Run selected Macro
            </button>
            </LockGate>
          {url && (
            <button
              type="button"
              onClick={clearUrl}
              data-testid="yt-clear-url"
              className="text-xs px-3 py-2 rounded-md border hairline text-[#6B6A68] hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center gap-1.5 shrink-0"
              title="Clear this URL and start over"
            >
              <X size={14} weight="bold" /> Clear
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-3 border-b hairline">
        <LockGate locked={ytLocked} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("youtube_twin")} block>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => fetchPull("metadata")}
            disabled={busy || !url.trim()}
            data-testid="yt-pull-metadata"
            className={`text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5 disabled:opacity-50 ${((busy && busyKind === "metadata") || transferBusy) ? "yt-glimmer" : ""}`}
          >
            <TextT size={12} weight="bold" /> Title / URL / Description
          </button>
          <button
            type="button"
            onClick={() => fetchPull("transcript")}
            disabled={busy || !url.trim()}
            data-testid="yt-pull-transcript"
            className="text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5 disabled:opacity-50"
          >
            <ChatTeardropDots size={12} weight="bold" /> Transcript
          </button>
          <button
            type="button"
            onClick={() => fetchPull("comments")}
            disabled={busy || !url.trim()}
            data-testid="yt-pull-comments"
            className="text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5 disabled:opacity-50"
          >
            <ListBullets size={12} weight="bold" /> Comments
          </button>
          {busy && <span className="text-xs text-[#6B6A68] flex items-center gap-1"><ArrowsClockwise size={12} className="animate-spin" /> Pulling…</span>}
        </div>
        </LockGate>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <label className="flex items-center gap-1.5 cursor-pointer text-[#6B6A68]">
              <input
                type="checkbox"
                checked={skipExisting}
                onChange={(e) => { setSkipExisting(e.target.checked); persistWb({ yt_skip_existing: e.target.checked }); }}
                data-testid="yt-skip-existing"
                className="accent-[#4A5D4E]"
              />
              Skip description text already in my silo
            </label>
            <InfoTip
              icon="eye"
              iconSize={16}
              placement="bottom-center"
              width={340}
              mobileCenter
              testid="yt-skip-existing-tip"
              text={"This filter is executed when pushing up to staging (button below). Use smart detection to only bring up to staging the new and unique Description data that belongs to this Video. No need to duplicate what already exists in your silo. Simply check the box, and we keep it clean for you."}
            />
          </span>
          <span className="inline-flex items-center gap-1.5">
            <label className="flex items-center gap-1.5 cursor-pointer text-[#6B6A68]">
              <input
                type="checkbox"
                checked={questionsOnly}
                onChange={(e) => { setQuestionsOnly(e.target.checked); persistWb({ yt_questions_only: e.target.checked }); }}
                data-testid="yt-questions-only"
                className="accent-[#4A5D4E]"
              />
              Comments - Smart Filter: questions only
            </label>
            <InfoTip
              icon="eye"
              iconSize={16}
              placement="bottom-center"
              width={340}
              mobileCenter
              testid="yt-questions-only-tip"
              text={"This filter is executed when pushing the Comments button above. Check this box to only pull in Comments you have already answered questions to. No point in pulling in the fluff of everyone's comments."}
            />
          </span>
          {questionsOnly && (
            <div className="flex items-center gap-1.5 flex-1 min-w-[180px]">
              <input
                type="text"
                value={creatorHandle}
                onChange={(e) => { setCreatorHandle(e.target.value); if (handleSaved) setHandleSaved(false); }}
                onBlur={() => persistWb({ yt_creator_handle: creatorHandle.trim() })}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveHandle(); } }}
                placeholder="Creator name/handle (must reply)"
                data-testid="yt-creator-handle"
                className="bg-white border hairline rounded-md px-2 py-1 text-xs flex-1 min-w-0 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              />
              <button
                type="button"
                onClick={saveHandle}
                data-testid="yt-creator-handle-save"
                title="Save this handle — kept until you change it, even after Clear Workbench"
                className={`shrink-0 text-[11px] px-2.5 py-1.5 rounded-md border flex items-center gap-1 transition-colors ${handleSaved ? "bg-[#EAF3EC] border-[#2F8F4E] text-[#2F8F4E]" : "bg-white hairline text-[#4A5D4E] hover:border-[#4A5D4E]"}`}
              >
                {handleSaved ? <><Check size={13} weight="bold" /> Saved</> : <><FloppyDisk size={13} weight="duotone" /> Save</>}
              </button>
            </div>
          )}
        </div>
        {skipExisting && (
          <div className="mt-1.5 text-[10px] text-[#6B6A68]">On transfer, the description is matched paragraph-by-paragraph (verbatim, all tiers) against your silo's existing content — repeats are dropped, Title &amp; URL always kept. Instant &amp; free, no AI.</div>
        )}
        {questionsOnly && (
          <div className="mt-1.5 text-[10px] text-[#6B6A68]">Returns only top-level comments that look like questions <em>and</em> have a reply from a commenter whose name matches the handle. Drastically reduces volume on viral videos.</div>
        )}
      </div>

      {/* Results pane */}
      <div className="px-4 py-3 bg-[#FDFCF8]" data-testid="yt-results-pane">
        {renderResults()}
      </div>

      {/* Transfer row */}
      {pull && (
        <div className="px-4 py-3 border-t hairline flex flex-wrap gap-2 items-center">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mr-1">Transfer to:</span>
          {transferTargets.map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={() => doTransfer(t)}
              disabled={transferBusy && t.label !== "Clear"}
              data-testid={t.testid}
              className="text-xs px-3 py-1.5 rounded-md border hairline bg-white text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1.5 disabled:opacity-50"
            >
              {/* Staging gets an UP arrow because it's "push the result UP into the
                  staging area at the top of the workbench" — visually intuitive. */}
              {t.label === "Staging" && <ArrowUp size={12} weight="bold" />}
              {t.label.startsWith("Slot") && <ArrowSquareIn size={12} weight="bold" />}
              {t.label === "Clear" && <Eraser size={12} weight="bold" />}
              {t.label}
            </button>
          ))}
          {transferBusy && <span className="text-[10px] text-[#6B6A68] flex items-center gap-1"><ArrowsClockwise size={11} className="animate-spin" /> Scanning silo…</span>}
        </div>
      )}

      {/* Skipped passages panel — shows description blocks dropped as duplicates on
          the last transfer (only populated when "Skip description text…" is on). */}
      {pull?.kind === "metadata" && skippedPassages.length > 0 && (
        <div className="px-4 py-3 border-t hairline bg-[#FDFCF8]" data-testid="yt-skipped-panel">
          <button
            type="button"
            onClick={() => setSkippedOpen((v) => !v)}
            data-testid="yt-skipped-toggle"
            className="w-full flex items-center gap-2 text-left text-[11px] uppercase tracking-[0.15em] text-[#6B6A68] hover:text-[#1C1B1A]"
          >
            {skippedOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
            Skipped lines
            <span className="tabular-nums text-[#C16E5A]">({skippedPassages.reduce((a, p) => a + p.split("\n").length, 0)})</span>
            <span className="ml-auto normal-case tracking-normal text-[10px] text-[#9A9890]">already in your silo</span>
          </button>
          {skippedOpen && (
            <div className="mt-3 space-y-2 max-h-60 overflow-y-auto" data-testid="yt-skipped-list">
              {skippedPassages.map((p, i) => (
                <div key={i} className="bg-white border hairline rounded p-2.5 text-[12px] leading-relaxed whitespace-pre-wrap text-[#6B6A68] line-through decoration-[#C16E5A]/40" data-testid={`yt-skipped-item-${i}`}>
                  {p}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============== Creator's Video Library ============== */}
      <div className="border-t hairline bg-white" data-testid="yt-library-section">
        <div className="px-4 py-3 border-b hairline flex items-center gap-3">
          <div className="w-7 h-7 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
            <FilmStrip size={14} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#1C1B1A]">Creator's Video Library{activeLibrary ? <span className="text-[#6B6A68] font-normal"> · {activeLibrary.channel_title} <span className="tabular-nums">({(activeLibrary.rows || []).filter((r) => !r.deleted).length})</span></span> : null}</div>
            <div className="text-xs text-[#6B6A68]">Paste a channel URL or @handle to scrape its full upload list. Strike-throughs and deletions persist across sessions.</div>
          </div>
          <button
            type="button"
            onClick={toggleLibCollapsed}
            data-testid="yt-lib-toggle"
            title={libCollapsed ? "Expand library" : "Collapse library"}
            className="ml-2 p-1.5 rounded-md text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-[#F0F1EE] flex items-center justify-center"
          >
            {libCollapsed ? <CaretRight size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
          </button>
        </div>

        {!libCollapsed && (
        <>
        <div className="px-4 py-3 border-b hairline space-y-3">
          <div className="min-w-0">
            <label className="block text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Channel URL or @handle</label>
            <input
              type="text"
              value={libChannelInput}
              onChange={(e) => setLibChannelInput(e.target.value)}
              placeholder="@yourchannel  ·  https://www.youtube.com/@yourchannel  ·  channel/UCxxx…"
              data-testid="yt-lib-channel-input"
              className="w-full bg-[#FDFCF8] border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
          </div>
          {/* From / To range + Fetch sit on the line BELOW the URL input — works
              better on mobile (no cramped inline row) and still fine on desktop. */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">From</label>
              <input
                type="number"
                min={1}
                value={libRangeFrom}
                onChange={(e) => setLibRangeFrom(e.target.value)}
                data-testid="yt-lib-from"
                className="w-20 bg-[#FDFCF8] border hairline rounded-md px-2 py-2 text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">To</label>
              <input
                type="number"
                min={1}
                value={libRangeTo}
                onChange={(e) => setLibRangeTo(e.target.value)}
                data-testid="yt-lib-to"
                className="w-20 bg-[#FDFCF8] border hairline rounded-md px-2 py-2 text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              />
            </div>
            {/* Oct 2026 (Randy) — the server has always refused this to anyone
                without the YouTube Digital Twin (`/yt/library/fetch` →
                `_require_creator_pro_sku`), but the button looked live, so a
                $15 creator could press it and get a 402 they can do nothing
                with. Same padlock + gate the pull buttons already carry. */}
            <LockGate locked={ytLocked} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("youtube_twin")}>
            <button
              type="button"
              onClick={fetchLibraryRange}
              disabled={libBusy || !libChannelInput.trim()}
              data-testid="yt-lib-fetch"
              className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5"
            >
              {libBusy ? <ArrowsClockwise size={12} className="animate-spin" /> : <FilmStrip size={12} weight="bold" />}
              {libBusy ? "Pulling…" : "Fetch range"}
            </button>
            </LockGate>
          </div>

          {libraries.length > 1 && (
            <div className="flex flex-wrap gap-1.5 text-xs items-center">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mr-1">Saved libraries:</span>
              {libraries.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setActiveLibId(l.id)}
                  data-testid={`yt-lib-tab-${l.channel_id}`}
                  className={`px-2.5 py-1 rounded-md border ${activeLibId === l.id ? "border-[#4A5D4E] bg-[#F0F1EE] text-[#1C1B1A]" : "hairline bg-white text-[#6B6A68] hover:text-[#4A5D4E]"}`}
                >
                  {l.channel_title} <span className="opacity-70">({(l.rows || []).filter((r) => !r.deleted).length}/{l.total_videos})</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {activeLibrary && (
          <div className="px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2 text-xs">
              <div className="text-[#1C1B1A] shrink-0">
                <span className="font-serif-display text-base">{activeLibrary.channel_title}</span>
                <span className="text-[#6B6A68] ml-2">
                  · {(activeLibrary.rows || []).filter((r) => !r.deleted).length.toLocaleString()} of {activeLibrary.total_videos.toLocaleString()} fetched
                  {activeLibrary.channel_handle && <> · <span className="font-mono">{activeLibrary.channel_handle}</span></>}
                </span>
              </div>
              {/* Feb 2026 (Randy pt-yt-lib-search) — inline search: name + URL */}
              <div className="flex-1 min-w-[200px] max-w-md">
                <div className="relative">
                  <input
                    type="text"
                    value={librarySearch}
                    onChange={(e) => setLibrarySearch(e.target.value)}
                    placeholder="Search this library by name or URL…"
                    data-testid="yt-lib-search-input"
                    className="w-full bg-white border hairline rounded-md pl-3 pr-16 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                  />
                  {librarySearch && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                      <span className="text-[10px] text-[#6B6A68] tabular-nums" data-testid="yt-lib-search-count">
                        {(activeLibrary.rows || []).filter((r) => !r.deleted && (
                          (r.title || "").toLowerCase().includes(librarySearch.toLowerCase()) ||
                          (r.video_id || "").toLowerCase().includes(librarySearch.toLowerCase())
                        )).length}
                      </span>
                      <button onClick={() => setLibrarySearch("")}
                        data-testid="yt-lib-search-clear"
                        className="text-[#6B6A68] hover:text-[#1C1B1A] text-[14px] leading-none">×</button>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => deleteLibrary(activeLibrary.id)}
                data-testid={`yt-lib-delete-${activeLibrary.id}`}
                className="text-[10px] uppercase tracking-[0.15em] text-[#C16E5A] hover:underline shrink-0"
                title="Delete this library entry (your strike-throughs will be lost)"
              >
                Delete library
              </button>
            </div>
            {(activeLibrary.rows || []).filter((r) => !r.deleted).length === 0 ? (
              <div className="text-xs text-[#6B6A68] italic py-6 text-center bg-[#FDFCF8] border hairline rounded">No videos yet — click <em>Fetch range</em> above.</div>
            ) : (
              <div className="border hairline rounded-md overflow-x-auto">
                <table className="w-full text-sm" data-testid="yt-lib-grid">
                  <thead className="bg-[#FDFCF8] text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Video Name</th>
                      <th className="text-right px-2 py-2 font-medium">URL</th>
                      <th className="text-right px-2 py-2 font-medium w-[112px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y hairline">
                    {(activeLibrary.rows || []).filter((r) => {
                      if (r.deleted) return false;
                      if (!librarySearch.trim()) return true;
                      const q = librarySearch.toLowerCase();
                      return (r.title || "").toLowerCase().includes(q) ||
                             (r.video_id || "").toLowerCase().includes(q);
                    }).map((row) => (
                      <tr key={row.video_id} className={`hover:bg-[#FDFCF8] ${row.struck ? "text-[#9A9890]" : ""}`} data-testid={`yt-lib-row-${row.video_id}`}>
                        <td className={`px-3 py-2 align-top break-words ${row.struck ? "line-through" : "text-[#1C1B1A]"}`}>
                          <span title={row.title || "(untitled)"}>{row.title || "(untitled)"}</span>
                        </td>
                        <td className={`px-2 py-2 align-top text-right ${row.struck ? "line-through" : ""}`}>
                          <a href={row.url} target="_blank" rel="noopener noreferrer" title={row.url} className="inline-block max-w-[220px] align-bottom truncate text-[11px] font-mono underline-offset-2 hover:underline">
                            {row.url}
                          </a>
                        </td>
                        <td className="px-2 py-2 text-right w-[112px] whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => transferUrlToSlot(activeLibrary.id, row)}
                              data-testid={`yt-lib-transfer-${row.video_id}`}
                              title="Send URL to the YouTube Tool URL slot above (strikes-through this row)"
                              className="p-1.5 rounded-md border hairline text-[#4A5D4E] hover:border-[#4A5D4E] hover:bg-[#F0F1EE]"
                            >
                              <ArrowUp size={13} weight="bold" />
                            </button>
                            <button
                              type="button"
                              onClick={() => updateRow(activeLibrary.id, row.video_id, { struck: !row.struck })}
                              data-testid={`yt-lib-strike-${row.video_id}`}
                              title={row.struck ? "Un-strike (mark as un-done)" : "Strike-through only (won't use this video)"}
                              className={`p-1.5 rounded-md border hairline ${row.struck ? "text-[#1C1B1A] bg-[#F0F1EE]" : "text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-[#F0F1EE]"}`}
                            >
                              <TextStrikethrough size={13} weight="bold" />
                            </button>
                            <button
                              type="button"
                              onClick={() => updateRow(activeLibrary.id, row.video_id, { deleted: true })}
                              data-testid={`yt-lib-delete-${row.video_id}`}
                              title="Delete this row (permanent)"
                              className="p-1.5 rounded-md border hairline text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] hover:bg-[#FDF1ED]"
                            >
                              <Trash size={13} weight="bold" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-2 text-[10px] text-[#6B6A68]">
              Buttons: <ArrowUp size={10} className="inline" weight="bold" /> Transfer URL to slot &amp; strike · <TextStrikethrough size={10} className="inline" weight="bold" /> Toggle strike-only · <Trash size={10} className="inline" weight="bold" /> Delete row.
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* Replace-guard dialog: shown when results pane has data and user wants a fresh pull */}
      {pendingFetch && pull && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="yt-replace-dialog">
          <div className="absolute inset-0 bg-[#1C1B1A]/60 backdrop-blur-sm" onClick={() => setPendingFetch(null)} />
          <div className="modal-safe relative w-full max-w-md bg-white border-2 border-[#C16E5A] rounded-xl shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#FDF1ED] text-[#C16E5A] flex items-center justify-center"><Info size={20} weight="duotone" /></div>
              <div>
                <div className="font-serif-display text-lg leading-none text-[#C16E5A]">Results pane already has data</div>
                <div className="text-xs text-[#6B6A68] mt-1">Transfer it somewhere first, or discard before the next pull.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {transferTargets.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => transferAndContinue(t)}
                  className="text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] text-left flex items-center gap-1.5"
                  data-testid={`yt-replace-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  Transfer to {t.label}
                </button>
              ))}
            </div>
            <div className="flex justify-between items-center mt-4 pt-3 border-t hairline">
              <button type="button" onClick={() => setPendingFetch(null)} className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
              <button type="button" onClick={discardAndContinue} className="text-xs text-[#C16E5A] hover:underline" data-testid="yt-replace-discard">Discard &amp; pull anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KeyEditor({ user, onSaved }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const hasKey = !!user?.has_youtube_key;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/auth/youtube-key", { youtube_api_key: value || null });
      toast.success(value ? "YouTube key saved." : "YouTube key cleared.");
      setValue("");
      onSaved?.(r.data);
    } catch {
      toast.error("Could not save key.");
    } finally { setSaving(false); }
  };

  const clearKey = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/auth/youtube-key", { youtube_api_key: null });
      toast.success("YouTube key cleared.");
      setValue("");
      onSaved?.(r.data);
    } catch { toast.error("Clear failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="px-4 py-3 border-b hairline bg-[#FDFCF8]" data-testid="yt-key-editor">
      <label className="block text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5 flex items-center gap-1.5">
        YouTube Data API v3 key
        <KeyInstructionsPopover keyType="youtube" testid="yt-key-instructions" onSaved={onSaved} />
      </label>
      <div className="flex gap-2">
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={hasKey ? "•••••••• (key on file — paste a new one to replace)" : "AIzaSy…"}
          data-testid="yt-key-input"
          className="flex-1 bg-white border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving || !value.trim()}
          data-testid="yt-key-save"
          className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {hasKey && (
          <button
            type="button"
            onClick={clearKey}
            disabled={saving}
            data-testid="yt-key-clear"
            className="text-xs px-3 py-2 rounded-md border hairline text-[#C16E5A] hover:border-[#C16E5A]"
          >
            Clear
          </button>
        )}
      </div>
      <div className="mt-2 text-[10px] text-[#6B6A68]">Stored only on your user record — never shared with other creators. Transcripts work without a key.</div>
    </div>
  );
}
